import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, KeyRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const PasswordChange = () => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("New passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: current, new_password: next }, { withCredentials: true });
      toast.success("Password changed. Use it on your next sign-in.");
      setOpen(false);
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative">
      <button
        data-testid="admin-password-button"
        onClick={() => setOpen(!open)}
        className="btn-ghost-gold inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs"
      >
        <KeyRound size={13} />
        Password
      </button>
      {open && (
        <form
          onSubmit={submit}
          data-testid="admin-password-form"
          className="absolute right-0 top-full mt-2 w-72 z-50 glass rounded-2xl p-5 shadow-2xl shadow-black/15"
        >
          <p className="font-display text-lg text-navy-900 mb-4">Change password</p>
          <div className="space-y-3">
            <input
              data-testid="admin-password-current"
              type="password"
              placeholder="Current password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="input-navy w-full rounded-xl px-3.5 py-2.5 text-sm"
              autoComplete="current-password"
            />
            <input
              data-testid="admin-password-new"
              type="password"
              placeholder="New password (min 8 chars)"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="input-navy w-full rounded-xl px-3.5 py-2.5 text-sm"
              autoComplete="new-password"
            />
            <input
              data-testid="admin-password-confirm"
              type="password"
              placeholder="Repeat new password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="input-navy w-full rounded-xl px-3.5 py-2.5 text-sm"
              autoComplete="new-password"
            />
          </div>
          <div className="mt-4 flex gap-2">
            <button type="submit" data-testid="admin-password-save" disabled={busy} className="btn-gold flex-1 rounded-full px-4 py-2 text-xs font-semibold disabled:opacity-60">
              {busy ? "Saving…" : "Save"}
            </button>
            <button type="button" data-testid="admin-password-cancel" onClick={() => setOpen(false)} className="btn-ghost-gold rounded-full px-4 py-2 text-xs">
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default function AdminTools() {
  return (
    <div className="flex items-center gap-2.5">
      <a
        href={`${API}/admin/enquiries/export.csv`}
        download="pacific-enquiries.csv"
        data-testid="admin-export-button"
        className="btn-ghost-gold inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs"
      >
        <Download size={13} />
        Export CSV
      </a>
      <PasswordChange />
    </div>
  );
}
